import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { OrderDetailContext } from 'compositions/OrderDetail/OrderDetailContext';
import type { SelectedServicesType } from 'compositions/Wizard/WizardContext';
import { WizardContext } from 'compositions/Wizard/WizardContext';
import { useGetPrices } from 'hooks/useGetPrices';
import { useGetTires } from 'hooks/useGetTires';
import { debounce } from 'lodash';
import type { AdditionalServicesType, CountriesType, UFOPart } from 'types/UFOTypes';
import { TIRE_DEFAULT_QTY } from 'utils/constantValues';
import { getMspn } from 'utils/getMspn';
import { BREAKPOINT } from 'utils/responsivenessUtils';
import hash from 'object-hash';
import { CentralProviderContext, CentralProviderState, Languages } from '@michelin/central-provider';
import { ProductAutocompleteComponent } from './ProductAutocomplete';
import type { SelectTiresAndPartsProps } from './SelectTiresAndParts';

const categoryIndex = {
  FLUID: 1,
  BRAKES: 2,
  ALIGNMENT: 3,
  INSPECTION: 4,
  TIRESERVICE: 5,
  MISC: 6,
} as const;

export const useSelectTiresAndParts = (params: SelectTiresAndPartsProps) => {
  const theme = useTheme();

  const {
    vehicle,
    setSelectedTires,
    fleet,
    provider,
    setSelectedServices,
    selectedTires,
    selectedServices,
    additionalServices,
    loading,
    order,
    commonPricesParams,
  } = params;

  const mobileView = useMediaQuery(theme.breakpoints.down(BREAKPOINT));
  const fleetId = fleet?.customer_number ?? '';
  const { vehicleIsEdited, emptyPqlPrices, setFittingTires: orderDetailFittingTires } = useContext(OrderDetailContext);
  const { setDisabled, setFittingTires: wizardContextFittingTires } = useContext(WizardContext);
  const { currentLanguage } = useContext<CentralProviderState>(CentralProviderContext);

  const [tabValue, setTabValue] = useState(vehicle ? 0 : 90);
  const [customParts, setCustomParts] = useState<UFOPart[]>([]);
  const [searchedPart, setSearchedPart] = useState<UFOPart | AdditionalServicesType>();

  let tireSizeId = 0;
  if (vehicleIsEdited && vehicle?.tire_size_id === 'custom')
    tireSizeId = Number(vehicle.speed_rating) ?? vehicle?.custom_tire_size_id ?? 0;
  if (vehicleIsEdited && vehicle?.tire_size_id !== 'custom') tireSizeId = Number(vehicle?.tire_size_id);
  if (!vehicleIsEdited)
    tireSizeId = vehicle?.custom_tire_size_id ?? Number(vehicle?.tire_size_id);

  const partsResponse = useGetTires(
    vehicle
      ? {
          country_code: currentLanguage === Languages.FR_CA ? 'ca' : (provider?.customer_country?.toLocaleLowerCase() as CountriesType) ?? 'us', // tires endpoint needs country code on lower
          with_service_mspn: true,
          tire_size_id: tireSizeId,
          custom_tire: vehicle?.tire_size_id === 'custom' || !!vehicle?.custom_tire_size_id,
          lump: true,
        }
      : null,
  );
  const parts = useMemo(() => partsResponse.data ?? [], [partsResponse.data]);

  const getSelectedServicesFromOrder = useCallback(() => {
    const services = order?.parts.filter((p) => !p.ufo_details?.length);
    if (!services) return null;
    const fixedServices: SelectedServicesType[] = [];
    services.forEach((s) => {
      const foundServ = fixedServices.findIndex((serv) => serv.part.mspn === getMspn(s));
      if (foundServ >= 0) {
        fixedServices[foundServ].lines.push({
          selected: s.selected ?? true,
          quantity: s.quantity,
          comment: s.comment || '',
          id: s.id,
          pql: s.pql_pricing && s.invoice_price ? Number(s.invoice_price) : undefined,
          index: fixedServices[foundServ].lines.length,
        });
      } else {
        fixedServices.push({
          part: {
            ...additionalServices.find((as) => as.mspn === s.mspn),
            description: s.part_description || s.description || '',
            mspn: s.mspn,
          },
          lines: [
            {
              selected: s.selected ?? true,
              quantity: s.quantity,
              comment: s.comment || '',
              id: s.id,
              pql: s.pql_pricing && s.invoice_price ? Number(s.invoice_price) : undefined,
              index: 0,
            },
          ],
        });
      }
    });
    return fixedServices;
  }, [additionalServices, order]);

  const getSelectedServices = useCallback(
    (ss: SelectedServicesType[]) => {
      const orderServices = getSelectedServicesFromOrder();
      if (!orderServices) return ss;
      const fixedServices = ss.map((service) => {
        const foundServ = orderServices.findIndex((s) => s.part.mspn === service.part.mspn);
        if (foundServ >= 0) return orderServices[foundServ];
        return service;
      });
      return fixedServices;
    },
    [getSelectedServicesFromOrder],
  );

  useEffect(() => {
    if (selectedServices.length === 0 && additionalServices.length > 0) {
      const SS = additionalServices.map((s) => ({
        part: s,
        lines: [
          {
            selected: false,
            quantity: 1,
            id: null,
            index: 0,
            comment: '',
          },
        ],
      }));
      if (order) setSelectedServices(getSelectedServices(SS));
      else setSelectedServices(SS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalServices, getSelectedServices, order, selectedServices.length, setSelectedServices]);

  const [refs, setRefs] = useState<HTMLLIElement>();
  useEffect(() => {
    if (parts.length > 0 && additionalServices.length > 0) {
      const locRefs = [...parts, ...additionalServices].reduce((acc, value) => {
        acc[value.mspn] = React.createRef();
        return acc;
      }, {} as HTMLLIElement);
      setRefs(locRefs);
    }
  }, [parts, additionalServices]);

  function instanceOfPart(data: UFOPart | AdditionalServicesType): data is AdditionalServicesType {
    return 'additional_information' in data;
  }
  useEffect(() => {
    if (searchedPart && instanceOfPart(searchedPart) && searchedPart?.additional_information?.category) {
      const { category } = searchedPart.additional_information;

      const categoryTab = categoryIndex[category] ?? 90;
      if (typeof categoryTab === 'number') {
        setTabValue(categoryTab);
        return;
      }
    }

    setTabValue(vehicle ? 0 : 90);
  }, [searchedPart, vehicle]);

  const [highlight, setHighlight] = useState(false);

  useEffect(() => {
    if (searchedPart) {
      const scrollToPosition = (id) => {
        setTimeout(() => {
          if (!refs || !refs[id].current) return;
          refs[id].current.scrollIntoView({
            behavior: 'smooth',
            block: 'end',
          });
        }, 500);
      };
      let cont = 0;
      scrollToPosition(searchedPart.mspn);
      const interval = setInterval(() => {
        cont += 1;
        setHighlight((prev) => !prev);
        if (cont > 16) {
          clearInterval(interval);
          setHighlight(false);
        }
      }, 150);
    }
  }, [searchedPart, refs]);

  const allPartsAndServices = useMemo(() => {
    if (vehicle && parts.length > 0) {
      return [
        // TODO: we should ensure the defaulted empty string isn't included here, but it better adheres to the typing than `undefined`

        ...additionalServices.map((p) => ({ mspn: getMspn(p) as string, qty: 1, is_pql: p.ispql ?? false })),

        ...parts.map((p) => ({ mspn: getMspn(p) as string, qty: TIRE_DEFAULT_QTY, is_pql: false })),
      ];
    }
    return [];
  }, [parts, vehicle, additionalServices]);


  const pricesResponse = useGetPrices(
    vehicle
      ? {
          ...commonPricesParams,
          parts: allPartsAndServices,
        }
      : null,
  );

  // TODO: ramifications of changing this to useState or useMemo should be investigated and the change should be made
  // eslint-disable-next-line react-hooks/exhaustive-deps
  let pricingErrorMspn: string[] = [];
  let error = false;

  const tabStyle = {
    overflow: mobileView ? 'scroll' : undefined,
    minHeight: 'calc(100% - 150px)',
    border: 'none',
    ...(mobileView ? { padding: 0 } : {}),
  };

  const addPricingErrorMspn = (mspn: string) => {
    if (!pricingErrorMspn.includes(mspn)) pricingErrorMspn = [...pricingErrorMspn, mspn];
  };

  if (pricesResponse.data?.parts?.length && pricesResponse.data?.parts?.length && !pricesResponse.isLoading) {
    pricesResponse.data?.parts.forEach((p) => {
      if (p.error) {
        const mspn = getMspn(p) ?? '';
        // we can't track something with no mspn - skip
        if (mspn === undefined) return;

        addPricingErrorMspn(mspn);
      }
    });

    allPartsAndServices.forEach((p) => {
      const mspn = getMspn(p) ?? '';
      // we can't track something with no mspn - skip
      if (mspn === undefined) return;

      if (
        !pricesResponse?.data?.parts.find((pr) => getMspn(pr) === mspn) &&
        !pricesResponse?.data?.pqlParts.find((pr) => getMspn(pr) === mspn)
      ) {
        addPricingErrorMspn(mspn);
      }
    });
  } else if (!pricesResponse.isLoading) error = true;

  const filteredParts = useMemo(() => {
    return parts?.filter((p) => {
      const mspn = getMspn(p) ?? '';

      return mspn !== undefined && !pricingErrorMspn.includes(mspn);
    });
  }, [parts, pricingErrorMspn]);

  const fittingTires = useMemo(() => parts?.map((tire) => tire.mspn) ?? [], [parts]);

  useEffect(() => {
    if (vehicle) {
      if (orderDetailFittingTires) orderDetailFittingTires(fittingTires);
      if (wizardContextFittingTires) wizardContextFittingTires(fittingTires);
    }
  }, [orderDetailFittingTires, wizardContextFittingTires, fittingTires, vehicle]);

  const filteredServices = additionalServices?.filter((p) => {
    const mspn = getMspn(p) ?? '';

    return mspn !== undefined && !pricingErrorMspn.includes(mspn);
  });

  useEffect(() => {
    if (vehicleIsEdited) {
      selectedTires.forEach((st) => {
        if (!filteredParts.some((fp) => getMspn(fp) === getMspn(st))) {
          setSelectedTires(
            selectedTires.map((t) => {
              if (getMspn(st) === getMspn(t)) {
                return { ...t, removed: true };
              }
              return t;
            }),
          );
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleIsEdited]);

  useEffect(() => {
    if (setDisabled) {
      setDisabled(
        loading ||
          (!selectedTires.some((st) => !st.removed) && !selectedServices.some((s) => s.lines.some((l) => l.selected))), // nothing selected
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash(selectedTires), selectedServices, loading, setDisabled]);

  const [pricesLoading, setPricesLoading] = useState(true);
  const checkLoading = useMemo(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    () =>
      debounce((newValue: boolean) => {
        setPricesLoading(newValue);
      }, 500),
    [],
  );

  useEffect(() => {
    checkLoading(partsResponse.isLoading || loading || pricesResponse.isLoading);
  }, [partsResponse.isLoading, loading, pricesResponse.isLoading, checkLoading]);

  const noProducts =
    filteredParts.length === 0 ||
    pricesResponse.data?.parts?.length === 0 ||
    !provider ||
    !fleet ||
    !parts ||
    !refs ||
    error;

  const ProductAutocomplete = () => (
    <ProductAutocompleteComponent
      customParts={customParts}
      filteredServices={filteredServices}
      onChange={(value) => {
        setSearchedPart(value);
      }}
      loading={partsResponse.isLoading || loading}
      parts={parts}
    />
  );

  return {
    pricesLoading,
    refs,
    filteredParts,
    noProducts,
    filteredServices,
    setTabValue,
    vehicleIsEdited,
    emptyPqlPrices,
    tabStyle,
    tabValue,
    searchedPart,
    highlight,
    setCustomParts,
    fleetId,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    ProductAutocomplete,
    fittingTires,
  };
};
